import React, { ChangeEvent, useEffect, useRef } from 'react'
import { APIProvider } from '@vis.gl/react-google-maps'
import { Button, InputField, Select, Tooltip } from '@enamelai/design-system'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import { US_STATE_OPTIONS } from '@constants/Providers'
import AddressField from '@components/common/AddressField/AddressField.component'
import useClinicInformation from './useClinicInformation'
import { getEnv } from '@utils/env'

import './ClinicInformationStyle.css'

const DEFAULT_CLINIC_LOGO_URL =
  'https://cdn-icons-png.flaticon.com/512/13712/13712994.png'
const GOOGLE_MAPS_API_KEY = getEnv('REACT_APP_GOOGLE_MAPS_API_KEY', '')

const SPECIALTIES = [
  { value: 'general', label: 'General' },
  { value: 'orthodontics', label: 'Orthodontics' },
  { value: 'pediatric', label: 'Pediatric' },
  { value: 'oral_surgery', label: 'Oral surgery' },
  { value: 'periodontics', label: 'Periodontics' },
  { value: 'endodontics', label: 'Endodontics' },
  { value: 'prosthodontics', label: 'Prosthodontics' },
]

const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'UTC-8 Pacific Standard Time (PST)' },
  { value: 'America/Denver', label: 'UTC-7 Mountain Standard Time (MST)' },
  { value: 'America/Chicago', label: 'UTC-6 Central Standard Time (CST)' },
  { value: 'America/New_York', label: 'UTC-5 Eastern Standard Time (EST)' },
  { value: 'Pacific/Honolulu', label: 'UTC-10 Hawaii Standard Time (HST)' },
]

const WEBSITE_PROTOCOL = 'https://'

function isLogoFile(file: File): boolean {
  const okMime =
    file.type === 'image/png' ||
    file.type === 'image/jpeg' ||
    file.type === 'image/svg+xml'
  const okExt = /\.(png|jpe?g|svg)$/i.test(file.name)
  return okMime || okExt
}

/** The DS phone field keeps 10 national digits, so drop a leading US country code. */
function toNationalDigits(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits
}

/** Keep the stored format: E.164 values stay E.164, others keep the DS format. */
function fromPhoneInput(formatted: string, current: string): string {
  const digits = formatted.replace(/\D/g, '')
  if (!digits) return ''
  return current.trim().startsWith('+') ? `+1${digits}` : formatted
}

function stripProtocol(url: string): string {
  return url.replace(/^https?:\/\//i, '')
}

function ClinicInformation(): React.ReactElement {
  const hooks = useClinicInformation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const disabled = hooks.loading || !hooks.clinicId

  useEffect(() => {
    hooks.setHeaderButtons(
      <>
        <Button
          className="settings-ds-button"
          variant="secondary"
          size="md"
          type="button"
          disabled={!hooks.hasChanges || disabled}
          onClick={hooks.handleReset}
        >
          Cancel
        </Button>
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          disabled={!hooks.hasChanges || disabled}
          onClick={() => {
            void hooks.handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )

    return () => {
      hooks.setHeaderButtons(null)
    }
  }, [
    hooks.handleReset,
    hooks.handleSave,
    hooks.hasChanges,
    hooks.loading,
    hooks.setHeaderButtons,
    disabled,
  ])

  const handleLogoInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !isLogoFile(file) || file.size > 2 * 1024 * 1024) return
    hooks.handleLogoChange(file)
  }

  const { data, setField, applyPlaceAddress, handleStateChange } = hooks

  return (
    <div
      className="clinic-information-settings"
      id="clinic-information-settings"
    >
      <form className="ci-form" onSubmit={(e) => e.preventDefault()}>
        <section className="ci-section">
          <div className="ci-section-header">
            <h2 className="ci-section-title">Practice details</h2>
            <Tooltip content="Your practice's name, specialty, and NPI.">
              <span className="ci-info-icon" aria-label="Practice details info">
                <InfoIcon aria-hidden="true" />
              </span>
            </Tooltip>
          </div>

          <div className="ci-logo-field">
            <span className="ci-label">Practice logo</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,.svg"
              className="ci-file-input"
              onChange={handleLogoInputChange}
              tabIndex={-1}
              disabled={disabled}
            />
            <div className="ci-logo-row">
              <div className="ci-logo-preview">
                <img
                  src={hooks.logoPreviewUrl}
                  alt="Practice logo"
                  className="ci-logo-preview-img"
                  onError={(e) => {
                    e.currentTarget.onerror = null
                    e.currentTarget.src = DEFAULT_CLINIC_LOGO_URL
                  }}
                />
              </div>
              <div className="ci-logo-actions">
                <Button
                  className="settings-ds-button"
                  variant="secondary"
                  size="xs"
                  type="button"
                  disabled={disabled}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload image
                </Button>
                <Button
                  className="settings-ds-button"
                  variant="secondary"
                  size="xs"
                  type="button"
                  destructive
                  disabled={disabled}
                  onClick={hooks.handleDeleteLogo}
                >
                  Delete image
                </Button>
                <p className="ci-hint">PNG, JPG or SVG (2MB Max.)</p>
              </div>
            </div>
          </div>

          <div className="ci-row ci-row--thirds">
            <InputField
              fullWidth
              variant="text"
              label="Practice name"
              placeholder="Enter practice name"
              value={data.practiceName}
              disabled={disabled}
              onChange={(e) => setField('practiceName', e.target.value)}
            />
            <Select
              label="Specialty"
              options={SPECIALTIES}
              value={data.specialty}
              disabled={disabled}
              onChange={(value) => setField('specialty', String(value))}
            />
            <InputField
              fullWidth
              variant="text"
              label="Practice NPI"
              placeholder="Enter NPI"
              value={data.practiceNpi}
              disabled={disabled}
              onChange={(e) => setField('practiceNpi', e.target.value)}
            />
          </div>
        </section>

        <section className="ci-section">
          <div className="ci-section-header">
            <h2 className="ci-section-title">Contact information</h2>
          </div>

          <div className="ci-row ci-row--thirds">
            <InputField
              fullWidth
              variant="phone"
              label="Phone number"
              value={toNationalDigits(data.phone)}
              disabled={disabled}
              onChange={(e) =>
                setField('phone', fromPhoneInput(e.target.value, data.phone))
              }
            />
            <InputField
              fullWidth
              variant="phone"
              label="Fax number"
              value={toNationalDigits(data.fax)}
              disabled={disabled}
              onChange={(e) =>
                setField('fax', fromPhoneInput(e.target.value, data.fax))
              }
            />
            <InputField
              fullWidth
              variant="email"
              label="Email"
              value={data.email}
              disabled={disabled}
              onChange={(e) => setField('email', e.target.value)}
            />
          </div>

          <InputField
            fullWidth
            variant="text"
            label="Website"
            placeholder="www.example.com"
            leadingIcon={
              <span className="ci-url-prefix">{WEBSITE_PROTOCOL}</span>
            }
            value={stripProtocol(data.website)}
            disabled={disabled}
            onChange={(e) => {
              const host = stripProtocol(e.target.value.trim())
              setField('website', host ? `${WEBSITE_PROTOCOL}${host}` : '')
            }}
          />
        </section>

        <section className="ci-section">
          <div className="ci-section-header">
            <h2 className="ci-section-title">Address</h2>
          </div>

          <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
            <AddressField
              ds
              className="ci-address-field"
              listboxId="ci-address-suggestions"
              label="Address line"
              placeholder="Enter an address"
              value={data.address1}
              countries={['US']}
              inputProps={{ disabled }}
              onChange={(value) => setField('address1', value)}
              onSelect={(_place, parsed) => applyPlaceAddress(parsed)}
            />
          </APIProvider>

          <InputField
            fullWidth
            variant="address"
            label="Address line"
            optional
            placeholder="Address line 2"
            value={data.address2}
            disabled={disabled}
            onChange={(e) => setField('address2', e.target.value)}
          />

          <div className="ci-row ci-row--city-state-zip">
            <InputField
              fullWidth
              variant="text"
              label="City"
              placeholder="Enter city"
              value={data.city}
              disabled={disabled}
              onChange={(e) => setField('city', e.target.value)}
            />
            <Select
              label="State"
              placeholder="Select state"
              options={US_STATE_OPTIONS}
              value={data.state}
              disabled={disabled}
              onChange={(value) => handleStateChange(String(value))}
            />
            <InputField
              fullWidth
              variant="text"
              label="ZIP"
              placeholder="Enter ZIP"
              value={data.zip}
              disabled={disabled}
              onChange={(e) => setField('zip', e.target.value)}
            />
          </div>

          <Select
            label="Timezone"
            options={TIMEZONES}
            value={data.timezone}
            disabled={disabled}
            onChange={(value) => setField('timezone', String(value))}
          />
        </section>
      </form>
    </div>
  )
}

export default ClinicInformation
